export const MIN_CAR_PAYOUT = 500
export const MAX_CAR_PAYOUT = 200_000

/** Slider resolution: finer steps for small amounts, coarser above. */
const STEP_RANGES = [
  { to: 5_000, step: 100 },
  { to: 20_000, step: 500 },
  { to: 60_000, step: 1_000 },
  { to: 120_000, step: 2_500 },
  { to: MAX_CAR_PAYOUT, step: 5_000 },
]

export interface StepRange {
  to: number
  step: number
  steps: number
  /** Lower end of the whole slider (same on every range). */
  min: number
}

/**
 * The step ranges from `minAmount` up to `maxAmount` (a lower cap, e.g.
 * 75.000 € for motorcycles, cuts the last range short; a lower floor, e.g.
 * 250 € for handbags, starts the first range earlier). The floor is kept
 * on the ranges so the conversions below share it.
 */
export function buildStepRanges(
  maxAmount = MAX_CAR_PAYOUT,
  minAmount = MIN_CAR_PAYOUT,
): StepRange[] {
  const min = Math.max(1, Math.min(minAmount, MAX_CAR_PAYOUT))
  const max = Math.max(min, Math.min(MAX_CAR_PAYOUT, maxAmount))
  const ranges: { to: number; step: number }[] = []
  for (const range of STEP_RANGES) {
    if (range.to <= min) continue
    if (range.to >= max) {
      ranges.push({ to: max, step: range.step })
      break
    }
    ranges.push(range)
  }
  return ranges.map((sr, i, srs) => ({
    ...sr,
    min,
    steps: Math.ceil((sr.to - (i > 0 ? srs[i - 1].to : min)) / sr.step),
  }))
}

const floorOf = (ranges: StepRange[]) => ranges[0]?.min ?? MIN_CAR_PAYOUT

export function totalSteps(ranges: StepRange[]): number {
  return ranges.reduce((sum, range) => sum + range.steps, 0)
}

export function loanAmountToSliderValue(
  loanAmount: number,
  ranges: StepRange[],
): number {
  const min = floorOf(ranges)
  if (loanAmount <= min) {
    return 0
  }

  for (let i = ranges.length - 1; i >= -1; i--) {
    const to = i >= 0 ? ranges[i].to : min
    if (loanAmount > to) {
      if (i === ranges.length - 1) return totalSteps(ranges)
      return (
        totalSteps(ranges.slice(0, i + 1)) +
        Math.ceil((loanAmount - to) / ranges[i + 1].step)
      )
    }
  }

  throw new Error()
}

export function sliderValueToLoanAmount(
  sliderValue: number,
  ranges: StepRange[],
): number {
  let loanAmount = floorOf(ranges)
  for (const { to, step, steps } of ranges) {
    loanAmount += Math.min(sliderValue, steps) * step
    sliderValue -= steps
    if (sliderValue <= 0) {
      return Math.min(loanAmount, to)
    }
  }

  return ranges[ranges.length - 1].to
}

/** Clamps an amount to the slider range and snaps it to the slider steps. */
export function snapLoanAmount(amount: number, ranges: StepRange[]): number {
  const max = ranges[ranges.length - 1].to
  const capped = Math.max(floorOf(ranges), Math.min(max, amount))
  return sliderValueToLoanAmount(
    loanAmountToSliderValue(capped, ranges),
    ranges,
  )
}
